import React, { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import { ChevronDownIcon } from "../../../helper/HwIcon";
import { adminApi } from "../api/adminApi";

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}><ChevronDownIcon /></components.DropdownIndicator>
);
const rsComponents = { DropdownIndicator, IndicatorSeparator: () => null };
const selectStyles = {
  control: (b, s) => ({
    ...b, minHeight: 40, borderRadius: 8, borderColor: s.isFocused ? "#3264FF" : "#e2e6ee",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(50,100,255,.1)" : "none", ":hover": { borderColor: "#3264FF" },
  }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
};

const CityManage = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countryId, setCountryId] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState(null);

  const defaultCountryId = (items) => {
    const uae = items.find((c) => /united arab emirates|uae/i.test(String(c.countryName || "")));
    if (uae) return String(uae.countryId);
    return items[0] ? String(items[0].countryId) : "";
  };
  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi("GET", "/admin/cities/manage");
      const items = res?.data?.data?.cities || [];
      setCities(items);
      setCountryId((cur) => cur || defaultCountryId(items));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const countries = useMemo(() => {
    const m = new Map();
    for (const c of cities) {
      if (c.countryId == null) continue;
      if (!m.has(String(c.countryId))) m.set(String(c.countryId), c.countryName || "");
    }
    return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [cities]);

  const visibleCount = useMemo(() => cities.filter((c) => c.visible).length, [cities]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return cities
      .filter((c) => (!countryId || String(c.countryId) === String(countryId)))
      .filter((c) => (!needle || String(c.cityName || "").toLowerCase().includes(needle) || String(c.countryName || "").toLowerCase().includes(needle)))
      .sort((a, b) =>
        (b.visible ? 1 : 0) - (a.visible ? 1 : 0) ||
        String(a.countryName || "").localeCompare(String(b.countryName || "")) ||
        String(a.cityName || "").localeCompare(String(b.cityName || ""))
      );
  }, [cities, countryId, q]);

  const toggle = async (city, next) => {
    setBusy(city.cityId);
    setCities((cs) => cs.map((c) => (c.cityId === city.cityId && c.countryId === city.countryId ? { ...c, visible: next } : c)));
    try {
      await adminApi("POST", "/admin/cities/manage", { cityId: city.cityId, countryId: city.countryId, visible: next });
    } catch {
      setCities((cs) => cs.map((c) => (c.cityId === city.cityId && c.countryId === city.countryId ? { ...c, visible: !next } : c)));
      setToast({ type: "err", text: "Could not update city" });
    } finally {
      setBusy(null);
    }
  };

  const setManyInView = async (next) => {
    const targets = shown.filter((c) => !!c.visible !== next);
    if (!targets.length) return;
    setCities((cs) => cs.map((c) => (targets.some((t) => t.cityId === c.cityId && t.countryId === c.countryId) ? { ...c, visible: next } : c)));
    try {
      await Promise.all(targets.map((t) => adminApi("POST", "/admin/cities/manage", { cityId: t.cityId, countryId: t.countryId, visible: next })));
      setToast({ type: "ok", text: next ? "Added cities" : "Removed cities" });
    } catch {
      setToast({ type: "err", text: "Some cities failed to update" });
      load();
    }
  };

  return (
    <div className="gp">
      {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}

      <div className="gp-head">
        <div>
          <h1 className="gp-title">City Manage</h1>
        </div>
      </div>

      <div className="gp-card">
        <div className="loc" style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", padding: "14px 16px" }}>
          <div style={{ minWidth: 240 }}>
            <label style={{marginBottom:5}}>Country</label>
            <Select
              options={[{ value: "", label: "All countries" }, ...countries]}
              value={countryId ? countries.find((c) => c.value === String(countryId)) || null : { value: "", label: "All countries" }}
              onChange={(o) => setCountryId(o?.value || "")}
              components={rsComponents}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={selectStyles}
            />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label >Search</label>
            <input className="inp" placeholder="Search city or country…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: "100%",height:42,marginTop:5 }} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn soft" onClick={() => setManyInView(true)}>Add all shown</button>
            <button className="btn soft" onClick={() => setManyInView(false)}>Remove all shown</button>
          </div>
        </div>

        <div className="gp-tablewrap">
          <table className="gp-t">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Show</th>
                <th>City</th>
                <th style={{ width: 220 }}>Country</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: "#98a2b6" }}>Loading…</td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: "#98a2b6" }}>No cities.</td></tr>
              ) : (
                shown.map((c) => (
                  <tr key={`${c.countryId}:${c.cityId}`} className={c.visible ? "row-sel" : ""}>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        className="chk"
                        checked={!!c.visible}
                        disabled={busy === c.cityId}
                        onChange={() => toggle(c, !c.visible)}
                      />
                    </td>
                    <td style={{ fontWeight: 600 }}>{c.cityName || "—"}</td>
                    <td style={{ color: "#5b6577" }}>{c.countryName || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CityManage;
